import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { defer, EMPTY, Observable } from 'rxjs';
import { catchError, expand } from 'rxjs/operators';

import { ErrorResponseType, WorkflowStepType, WorkflowStepResultType } from '../models';

import { DECISION_MODEL_STATE_ENUMERATION, DECISION_MODEL_STEPS_CONSTANTS, redirectDecisionModel } from '../configs';
import { ROUTES_CONSTANTS } from '@shared/configs';

import { InitialStep, DecisionModelStep, FinalStep } from './steps';

@Injectable({ providedIn: 'root' })
export class DecisionModelWorkflowOrchestrator {
  private steps: Map<string, WorkflowStepType> = new Map();
  private readonly router: Router;

  constructor(private injector: Injector) {
    this.steps.set(DECISION_MODEL_STEPS_CONSTANTS.INITIAL_STEP, this.injector.get(InitialStep));
    this.steps.set(DECISION_MODEL_STEPS_CONSTANTS.DECISION_STEP, this.injector.get(DecisionModelStep));
    this.steps.set(DECISION_MODEL_STEPS_CONSTANTS.FINAL_STEP, this.injector.get(FinalStep));

    this.router = this.injector.get(Router);
  }

  public run(
    injector: Injector,
    initialStep: string = DECISION_MODEL_STEPS_CONSTANTS.INITIAL_STEP
  ): Observable<WorkflowStepResultType> {
    let currentStep = initialStep;

    return defer(() =>
      this.executeStep(currentStep, injector).pipe(
        expand((result) => {
          if (!result.success || !result.nextStep) return EMPTY;
          currentStep = result?.nextStep;
          return this.executeStep(currentStep, injector);
        }),
        catchError((err: ErrorResponseType) => {
          this.redirectTo(err);
          return EMPTY;
        })
      )
    );
  }

  private executeStep(stepName: string, injector: Injector): Observable<WorkflowStepResultType> {
    const step = this.steps.get(stepName);
    if (!step) throw new Error(`Step ${stepName} not found`);
    return step.execute(injector);
  }

  private redirectTo(err: ErrorResponseType): void {
    if (err?.error?.processState) {
      redirectDecisionModel({
        state: err.error.processState as DECISION_MODEL_STATE_ENUMERATION,
        injector: this.injector,
      });
      return;
    }
    this.router.navigate([ROUTES_CONSTANTS.GRAY_ZONE]);
  }
}
